import BCMSCurrency from "../../../BCMSCurrency/";
import BCMSDate from "../../../BCMSDate/";
import BCMSRecord from "../..";

export default class BCMSRecordProperty {
    
    _id;            // ID of record stored by instance
    _modelPropID;   // ID of propertes stored by model
    _getter;        // Getter function of record 
    _setter;        // Setter function of record
    _toRecord;      // Defines function used to set value from instance for an object that can be used in model

    // CONSTRUCTOR :: STRING, STRING, (VOID -> *), (STRING -> VOID), (bcmsRecord -> STRING) -> this
    constructor(id, modelPropID, getter, setter, toRecord) {
        this.id = id;
        this.modelPropID = modelPropID
        this.getter = getter;
        this.setter = setter;
        this.toRecord = toRecord;
    }

    // OBJECT:record, OBJECT:obj -> OBJECT:obj
    // Binds value from record to object using a set getter function:
    // NOTE: Given object already needs to have bound getter/setters
    initRecordFrom(record, obj) {
        // Value needs to be set before we can add setters and getters to property
        this.setter.call(obj, record[this.modelPropID]);

        return Object.defineProperty(obj, this.id, {
            "get":() => this.getter.call(obj),
            "set":(value) => this.setter.call(obj, value),
        });
    }

    // :: OBJECT:record, OBJECT:obj -> OBJECT:record
    // Binds value to record from object using set "toRecord" function:
    initRecordTo(record, obj) {
        record[this.modelPropID] = this.toRecord.call(obj, obj);
        return record;
    }

    /**
     *
     *  Getters 
     * 
     */

    // :: VOID -> STRING
    get id() {
        return this._id;
    }

    // :: VOID -> STRING
    get modelPropID() {
        return this._modelPropID;
    }

    // :: VOID -> (VOID -> *)
    get getter() {
        return this._getter;
    }

    // :: VOID -> (STRING -> VOID)
    get setter () {
        return this._setter;
    }

    // :: VOID -> (VOID -> STRING)
    get toRecord() {
        return this._toRecord;
    }

    /**
     *
     *  Setters 
     * 
     */

    // :: STRING -> VOID
    set id(value) {
        this._id = value;
    }

    // :: STRING -> VOID
    set modelPropID(value) {
        this._modelPropID = value;
    }

    // :: (VOID -> *) -> VOID
    set getter(value) {
        this._getter = value;
    }

    // :: (STRING -> VOID) -> VOID
    set setter(value) {
        this._setter = value;
    }

    // :: (VOID -> STRING) -> VOID
    set toRecord(value) {
        this._toRecord = value;
    }

    /**
     * 
     *
     *  Staitc Methods
     *  
     */


    // :: STATIC FACTORY :: {STRING:instancePropID, STRING:modelPropID, FUNCTION:getter, FUNCTION:setter, FUNCTION:toRecord} -> bcmsRecordProperty
    static init({id, modelPropID, getter, setter, toRecord}) {
        return new BCMSRecordProperty(id, modelPropID, getter, setter, toRecord);
    }
    static mapTo({id,instanceField,modelPropID,getter,setter,toRecord}){    
       return  BCMSRecordProperty.init({ 
            "id":id,
            "modelPropID":modelPropID ?? id,
            "getter":getter ?? function(){
                return this[instanceField]
            }, 
            "setter":setter ?? function(value){
                this[instanceField] = value
            },
            "toRecord":toRecord ?? function(){
                return this[instanceField]? this[instanceField]:""
            }
        })
    }
    static mapToClass(id,instanceField,BCMSRecordClass,modelPropID){       
        return  BCMSRecordProperty.mapTo({
               "id":id,
               "modelPropID":modelPropID,
               "instanceField":instanceField,          
                "setter":function(value){
                    let recordClass = BCMSRecordClass();
                    this[instanceField] = value instanceof recordClass 
                        ? value 
                        : recordClass.globalController(this.constructor._GlobalController).initByID(value)
                },       
                "toRecord":function(){
                    return this[instanceField] ? this[instanceField].id:""
                }
            })       
    }

    static mapToNumber(id,instanceField,modelPropID){
        return BCMSRecordProperty.mapTo({ 
            "id":id,
            "modelPropID":modelPropID,
            "instanceField":instanceField,         
            "setter":function(value){ 
                this[instanceField] = isNaN(value) ? undefined : parseFloat(value)
             },
            "toRecord":function(){
                return this[instanceField] !== undefined ? this[instanceField].toString() : "";
            }
        })  
    }
    static mapToArray(id,instanceField, propertyType, modelPropID){
        return BCMSRecordProperty.mapTo({ 
            "id":id,
            "modelPropID":modelPropID,
            "instanceField":instanceField,         
            "setter": function(value) {
                if(value === undefined || Array.isArray(value)) { this[instanceField] = value; }
				else {
                    const arrayValues = value.length > 0 ? value.replace(/ /g, "").split(",") : [];
                    this[instanceField] = arrayValues.map(item => propertyType === "number" ? parseFloat(item) : item, []);
                }

            },
            "toRecord": function() {                
                return this[instanceField] === undefined || (Array.isArray(this[instanceField]) && this[instanceField].length === 0)
                    ? ""
                    : this[instanceField].reduce((result, item) => `${ result },${ item.toString() }`, "").slice(1);
            }
        })  
    }

    static mapToClassArray(id, instanceField, BCMSRecordClass, modelPropID) {
        return BCMSRecordProperty.mapTo({
            "id":id,
            "modelPropID":modelPropID,
            "instanceField":instanceField,         
            "setter": function(value) {
                if(value === undefined || Array.isArray(value)) { this[instanceField] = value; }
                else {
                    const recordClass = BCMSRecordClass();

                    this[instanceField] = (value.length > 0 ? value.replaceAll(" ", "").split(",") : []).map(id => {
                        return recordClass.globalController(this.constructor._GlobalController).initByID(id);
                    }, []);
                }
            },
            "toRecord": function() {
                return this[instanceField] === undefined || (Array.isArray(this[instanceField]) && this[instanceField].length === 0)
                    ? ""
                    : this[instanceField].reduce((result, item) => `${ result },${ item.id.toString() }`, "").slice(1);
            }
        });
    }

    static mapToString(id,instanceField,modelPropID){
        return BCMSRecordProperty.mapTo({id,instanceField,modelPropID})      
    }
    static mapToBoolean(id,instanceField,modelPropID){
        return BCMSRecordProperty.mapTo({ 
            "id":id,
            "modelPropID":modelPropID,
            "instanceField":instanceField,         
            "setter":function(value){this[instanceField] = (value === true || value === "true")},
            "toRecord":function(){
                return this[instanceField]? "true":"false"
            }
        })       
    }
    static mapToCurrency(id,instanceField,modelPropID){
        return BCMSRecordProperty.mapTo({ 
            "id":id,
            "modelPropID":modelPropID,
            "instanceField":instanceField,         
            "setter":function(value){this[instanceField] = BCMSCurrency.init(value)},
            "toRecord":function(){
                return this[instanceField]? this[instanceField].amount:""
            }
        })       
    } 
    
    static mapToDate(propID,instanceField,modelPropID){        
        return BCMSRecordProperty.mapTo( { 
        "id":propID,
        "modelPropID":modelPropID,
        "instanceField":instanceField,         
        "setter":function(value){
            this[instanceField] = value instanceof BCMSDate 
            ? value 
            : BCMSDate.init().offset(value??0)
        },
        "toRecord":function(){            
            return this[instanceField] ? this[instanceField].diff():""
        }
        })       
    }   

}